import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, MapPin, Calendar, Check, Truck, Mic, Info } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useContent } from "../context/ContentContext";
import { supabase, formatSupabaseError } from "../lib/supabase";
import { LOGO_URL } from "../lib/data";
import Footer from "../components/Footer";

const SESSION_OPTIONS = [
  { value: "recording", label: "Aufnahme (Recording bei dir vor Ort)" },
  { value: "mix", label: "Mix (Stems mixen)" },
  { value: "master", label: "Master (finale Politur)" },
  { value: "mix-master", label: "Mix + Master (Paket)" },
  { value: "beat", label: "Beat / Production (Custom Beat)" },
  { value: "consultation", label: "Beratung / Pre-Production" },
  { value: "other", label: "Sonstiges" },
];

const DURATIONS = [
  { value: "1-2h", label: "1 – 2 Stunden" },
  { value: "2-4h", label: "2 – 4 Stunden" },
  { value: "halbtag", label: "Halbtag (4 – 6h)" },
  { value: "tag", label: "Ganzer Tag (8h+)" },
  { value: "mehrtag", label: "Mehrtägig" },
  { value: "remote", label: "Remote / asynchron" },
];

const initial = {
  name: "",
  email: "",
  phone: "",
  artist_name: "",
  location: "",
  preferred_date: "",
  preferred_time: "",
  session_type: "recording",
  duration: "",
  message: "",
  consent: false,
};

const todayISO = () => {
  const d = new Date();
  d.setDate(d.getDate() + 2);
  return d.toISOString().slice(0, 10);
};

const DEFAULT_STUDIO_SERVICES = [
  { code: "01", title: "Aufnahme", price: "ab 49 €", sub: "/ Session", description: "Recording-Session bei dir vor Ort. Inkl. Mikrofon, Interface, Laptop und allem was wir brauchen. Vocal-Booth & Akustik richten sich nach den Gegebenheiten bei dir.", cta: "Session anfragen" },
  { code: "02", title: "Mix", price: "ab 89 €", sub: "/ Song", description: "Professioneller Mix Inhouse. Stems aufnehmen wir vor Ort oder du schickst sie zu — Pegelausgleich, Effekte, räumliche Tiefe.", cta: "Mix anfragen" },
  { code: "03", title: "Master", price: "ab 39 €", sub: "/ Song", description: "Mastering Inhouse — Loudness, Druck, finale Politur fürs Streaming. Liefer-Master & Stem-Master inklusive.", cta: "Master anfragen" },
  { code: "04", title: "Mix + Master", price: "ab 119 €", sub: "/ Song", description: "Mix und Master als Paket. Beides aus einer Hand, eine Sound-Signature, ein klarer Preis.", cta: "Mix + Master anfragen" },
  { code: "05", title: "Beats", price: "Auf Anfrage", sub: "/ individuell", description: "Maßgeschneiderte Beats aus unserem Producer-Roster. Genre & Stimmung nach Briefing — Pricing nach Umfang und Nutzungsrechten.", cta: "Beat anfragen" },
];

export default function StudioPage() {
  const { content } = useContent();
  const studio = content?.studio_page || {};
  const services = studio.services && studio.services.length > 0 ? studio.services : DEFAULT_STUDIO_SERVICES;

  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const scrollToForm = () => {
    document.getElementById("session-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.location || !form.preferred_date || !form.session_type) {
      toast.error("Bitte alle Pflichtfelder ausfüllen.");
      return;
    }
    if (!form.consent) {
      toast.error("Bitte DSGVO-Einwilligung bestätigen.");
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.from("studio_bookings").insert([
        {
          name: form.name,
          email: form.email,
          phone: form.phone || null,
          artist_name: form.artist_name || null,
          location: form.location,
          preferred_date: form.preferred_date,
          preferred_time: form.preferred_time || null,
          session_type: form.session_type,
          duration: form.duration || null,
          message: form.message || null,
          consent: true,
        },
      ]);
      if (error) throw error;
      toast.success("Session-Anfrage gesendet. Wir melden uns innerhalb 48h.");
      setForm(initial);
      setSent(true);
    } catch (err) {
      let msg = formatSupabaseError(err, "Übertragung fehlgeschlagen.");
      // Supabase wraps 404s in a body-stream error if the table is missing
      const looksLikeMissingTable =
        msg.toLowerCase().includes("body stream") ||
        msg.toLowerCase().includes("not found") ||
        msg.toLowerCase().includes("schema") ||
        err?.code === "42P01" ||
        err?.status === 404;
      if (looksLikeMissingTable) {
        msg =
          "Die Datenbank-Tabelle 'studio_bookings' fehlt in Supabase. Bitte einmalig die Migration ausführen (SQL aus der letzten Anweisung).";
      }
      toast.error(msg, { duration: 8000 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white grain">
      {/* Header — matches main site (logo center, session CTA underneath) */}
      <header className="sticky top-0 z-40 bg-[#050505]/85 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-3 md:py-4 grid grid-cols-[1fr_auto_1fr] items-center gap-8 lg:gap-12">
          <Link
            to="/"
            data-testid="studio-back"
            className="inline-flex items-center gap-2 text-[10px] md:text-[11px] font-semibold tracking-[0.25em] uppercase text-zinc-400 hover:text-white transition-colors justify-self-start"
          >
            <ArrowLeft size={14} /> Zurück
          </Link>

          <div className="flex flex-col items-center gap-3 md:gap-3.5 shrink-0">
            <Link to="/" className="group shrink-0">
              <img
                src={LOGO_URL}
                alt="STRAßENWERT"
                className="h-12 md:h-14 w-auto object-contain drop-shadow-[0_0_18px_rgba(255,0,51,0.4)] group-hover:drop-shadow-[0_0_28px_rgba(255,0,51,0.7)] transition-all duration-500"
              />
              <span className="sr-only">Straßenwert</span>
            </Link>
            <button
              type="button"
              data-testid="studio-header-cta"
              onClick={scrollToForm}
              className="inline-flex items-center gap-2 text-[9px] md:text-[10px] font-bold tracking-[0.3em] uppercase border border-white/15 px-3.5 md:px-4 py-1.5 md:py-2 hover:border-[#ff0033]/60 hover:shadow-[0_0_20px_rgba(255,0,51,0.25)] transition-all whitespace-nowrap"
            >
              <span className="red-led" />
              Session
            </button>
          </div>

          <div className="justify-self-end w-8 md:w-12" />
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden py-20 md:py-32 px-6 md:px-12">
        <div className="absolute top-1/4 left-1/3 -translate-x-1/2 w-[40rem] h-[40rem] rounded-full bg-[#ff0033]/12 blur-[160px] pointer-events-none" />
        <div className="absolute top-0 left-0 right-0 h-px hairline-red opacity-50" />

        <div className="relative max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
          >
            <div className="flex items-center gap-3 mb-6 text-[11px] tracking-[0.4em] uppercase text-zinc-500 font-semibold">
              <Truck size={14} className="text-[#ff0033]" />
              {studio.eyebrow || "Mobile Studio · Berlin & Umgebung"}
            </div>
            <h1 className="font-display font-black uppercase tracking-tighter leading-[0.88] text-[11vw] sm:text-7xl lg:text-8xl">
              {studio.title_a || "Wir kommen"} <br />
              <span className="text-glow-red-strong text-white">{studio.title_b || "zu dir."}</span>
            </h1>
            <p className="mt-8 max-w-2xl text-base md:text-lg text-zinc-400 leading-relaxed">
              {studio.intro ||
                "Unser festes Studio ist im Aufbau — bis dahin packen wir alles ein und kommen zu dir. Recording-Stack, Interface, Mikrofone, Akustik — komplette Session bei dir zu Hause. Mix & Master folgen Inhouse."}
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <button
                onClick={scrollToForm}
                data-testid="studio-hero-cta"
                className="group relative inline-flex items-center gap-3 bg-white text-black px-6 md:px-7 py-3.5 md:py-4 text-[10px] md:text-[11px] font-bold tracking-[0.3em] uppercase overflow-hidden hover:shadow-[0_0_24px_rgba(255,0,51,0.4)] transition-all"
              >
                <span className="relative z-10 group-hover:text-white transition-colors">
                  Session anfragen
                </span>
                <ArrowUpRight size={14} className="relative z-10 group-hover:text-white group-hover:rotate-45 transition-all duration-500" />
                <span className="absolute inset-0 bg-[#ff0033] translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
              </button>
              <a
                href="#leistungen-studio"
                className="inline-flex items-center gap-3 border border-white/20 px-6 md:px-7 py-3.5 md:py-4 text-[10px] md:text-[11px] font-bold tracking-[0.3em] uppercase text-zinc-200 hover:border-[#ff0033]/60 hover:text-white hover:shadow-[0_0_20px_rgba(255,0,51,0.2)] transition-all"
              >
                Leistungen & Preise
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section id="leistungen-studio" className="relative bg-[#070707] py-20 md:py-32 px-6 md:px-12 overflow-hidden grain">
        <div className="relative max-w-7xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div>
              <div className="flex items-center gap-3 mb-5 text-[11px] tracking-[0.4em] uppercase text-zinc-500 font-semibold">
                <span className="block w-10 h-px bg-[#ff0033] shadow-[0_0_10px_#ff0033]" />
                Studio · Leistungen & Preise
              </div>
              <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl md:text-5xl lg:text-6xl">
                {studio.services_title || "Was wir machen."}
              </h2>
            </div>
            <p className="text-zinc-500 text-sm max-w-sm leading-relaxed">
              {studio.services_intro ||
                "Preise sind Startpunkte. Mix & Master sind Add-Ons — Pricing individuell je nach Umfang. Endgültige Pakete vereinbaren wir nach kurzem Briefing."}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {services.map((s, i) => (
              <motion.article
                key={(s.code || s.title) + i}
                data-testid={`studio-service-${s.code || i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: i * 0.06, duration: 0.7 }}
                className="group relative bg-[#0A0A0A] border border-white/[0.06] p-6 md:p-7 flex flex-col min-h-[280px] md:min-h-[360px] transition-all duration-500 hover:border-[#ff0033]/40 hover:-translate-y-1 hover:shadow-[0_20px_60px_-20px_rgba(255,0,51,0.4)]"
              >
                <div className="absolute top-5 right-5 red-led opacity-50 group-hover:opacity-100 transition-opacity" />
                <div className="flex items-baseline gap-3">
                  <span className="font-display font-black text-[#ff0033] text-xs tracking-[0.2em]">{s.code || String(i + 1).padStart(2, "0")}</span>
                  <span className="block w-12 h-px bg-white/15" />
                </div>
                <h3 className="font-display font-black uppercase text-2xl md:text-3xl mt-6 leading-none break-words">{s.title}</h3>
                <div className="mt-4 flex items-baseline gap-2 flex-wrap">
                  <span className="font-display font-bold text-xl md:text-2xl text-white">{s.price}</span>
                  {s.sub && <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">{s.sub}</span>}
                </div>
                {s.description && (
                  <p className="mt-5 text-sm text-zinc-400 leading-relaxed flex-1">{s.description}</p>
                )}
                <button
                  type="button"
                  data-testid={`studio-service-cta-${s.code || i}`}
                  onClick={scrollToForm}
                  className="mt-6 inline-flex items-center justify-between gap-3 text-[11px] tracking-[0.25em] uppercase font-bold text-white border-t border-white/10 pt-5 group/btn"
                >
                  <span>{s.cta || "Session anfragen"}</span>
                  <ArrowUpRight size={16} className="text-[#ff0033] group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1 transition-transform duration-300" />
                </button>
              </motion.article>
            ))}
          </div>

          <div className="mt-10 inline-flex items-start gap-3 border border-[#ff0033]/25 bg-[#ff0033]/[0.04] p-5 max-w-3xl">
            <Info size={14} className="text-[#ff0033] mt-0.5 shrink-0" />
            <p className="text-xs text-zinc-300 leading-relaxed">
              <strong className="text-white">Mix &amp; Master</strong> können nach der Recording-Session dazu gebucht werden — der Preis hängt von Umfang, Stems und Aufwand ab. Du bekommst nach der Session ein klares Angebot.
            </p>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section id="session-form" className="relative py-20 md:py-32 px-6 md:px-12 overflow-hidden">
        <div className="absolute bottom-0 right-0 w-[30rem] h-[30rem] rounded-full bg-[#ff0033]/10 blur-[140px] pointer-events-none" />

        <div className="relative max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-5 text-[11px] tracking-[0.4em] uppercase text-zinc-500 font-semibold">
            <Mic size={14} className="text-[#ff0033]" />
            Session-Anfrage
          </div>
          <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl md:text-5xl lg:text-6xl">
            {studio.form_title || "Wann passt's bei dir?"}
          </h2>
          <p className="mt-5 max-w-2xl text-sm md:text-base text-zinc-400 leading-relaxed">
            {studio.form_intro ||
              "Wir kommen mit komplettem Equipment vorbei. Sag uns wann und wo — wir melden uns innerhalb 48h mit Bestätigung oder Alternativtermin."}
          </p>

          {sent ? (
            <div data-testid="studio-booking-success" className="mt-12 border border-[#ff0033]/30 bg-[#ff0033]/[0.04] p-10 text-center">
              <div className="red-led mx-auto mb-4" />
              <div className="font-display font-black uppercase text-3xl mb-3">Anfrage eingegangen</div>
              <div className="text-sm text-zinc-400 max-w-md mx-auto leading-relaxed">
                Wir prüfen deine Anfrage und melden uns innerhalb 48h per E-Mail. Bei Bestätigung kommen wir mit komplettem Setup zu dir.
              </div>
              <button
                type="button"
                onClick={() => setSent(false)}
                className="mt-7 text-[10px] tracking-[0.3em] uppercase border border-white/15 px-5 py-3 hover:border-[#ff0033]/50 text-zinc-300 hover:text-white"
              >
                Weitere Session anfragen
              </button>
            </div>
          ) : (
            <form
              data-testid="studio-page-form"
              onSubmit={onSubmit}
              noValidate
              className="mt-10 md:mt-12 bg-[#0A0A0A] border border-white/[0.08] p-6 md:p-10"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-7">
                <BookingField label="Name *">
                  <input data-testid="sp-name" required value={form.name} onChange={handle("name")} className="booking-input" placeholder="Max Mustermann" />
                </BookingField>
                <BookingField label="Künstlername">
                  <input data-testid="sp-artist" value={form.artist_name} onChange={handle("artist_name")} className="booking-input" placeholder="Optional" />
                </BookingField>

                <BookingField label="E-Mail *">
                  <input data-testid="sp-email" type="email" required value={form.email} onChange={handle("email")} className="booking-input" placeholder="name@email.com" />
                </BookingField>
                <BookingField label="Telefon">
                  <input data-testid="sp-phone" value={form.phone} onChange={handle("phone")} className="booking-input" placeholder="+49 ..." />
                </BookingField>

                <BookingField label="Ort / Adresse *" icon={MapPin} full>
                  <input data-testid="sp-location" required value={form.location} onChange={handle("location")} className="booking-input" placeholder="Straße, PLZ, Stadt" />
                </BookingField>

                <BookingField label="Wunschdatum *" icon={Calendar}>
                  <input data-testid="sp-date" type="date" required min={todayISO()} value={form.preferred_date} onChange={handle("preferred_date")} className="booking-input" />
                </BookingField>
                <BookingField label="Uhrzeit (optional)">
                  <input data-testid="sp-time" value={form.preferred_time} onChange={handle("preferred_time")} className="booking-input" placeholder="z.B. ab 14:00" />
                </BookingField>

                <BookingField label="Was brauchst du? *" full>
                  <Select value={form.session_type} onValueChange={(v) => setForm((f) => ({ ...f, session_type: v }))}>
                    <SelectTrigger
                      data-testid="sp-session-trigger"
                      className="bg-transparent border-0 border-b border-white/15 rounded-none h-12 px-0 text-white font-medium tracking-wide focus:ring-0 focus:border-[#ff0033]/60 hover:border-white/30 transition-colors"
                    >
                      <SelectValue placeholder="Auswählen…" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border border-white/10 text-white">
                      {SESSION_OPTIONS.map((t) => (
                        <SelectItem key={t.value} value={t.value} className="focus:bg-[#ff0033]/15 focus:text-white">
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </BookingField>

                <BookingField label="Dauer">
                  <Select value={form.duration} onValueChange={(v) => setForm((f) => ({ ...f, duration: v }))}>
                    <SelectTrigger
                      data-testid="sp-duration-trigger"
                      className="bg-transparent border-0 border-b border-white/15 rounded-none h-12 px-0 text-white font-medium tracking-wide focus:ring-0 focus:border-[#ff0033]/60 hover:border-white/30 transition-colors"
                    >
                      <SelectValue placeholder="Auswählen…" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border border-white/10 text-white">
                      {DURATIONS.map((t) => (
                        <SelectItem key={t.value} value={t.value} className="focus:bg-[#ff0033]/15 focus:text-white">
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </BookingField>

                <BookingField label="Vision / Anmerkungen" full>
                  <textarea
                    data-testid="sp-message"
                    rows={4}
                    value={form.message}
                    onChange={handle("message")}
                    className="booking-input resize-none"
                    placeholder="Was hast du vor? Stil, Referenzen, besondere Wünsche, Aufnahmesituation vor Ort…"
                  />
                </BookingField>
              </div>

              <label className="mt-7 flex items-start gap-4 cursor-pointer group" data-testid="sp-consent">
                <span
                  className={`relative shrink-0 mt-0.5 flex items-center justify-center w-5 h-5 border transition-all ${
                    form.consent
                      ? "bg-[#ff0033] border-[#ff0033] shadow-[0_0_12px_rgba(255,0,51,0.5)]"
                      : "bg-transparent border-white/20 group-hover:border-white/50"
                  }`}
                >
                  {form.consent && <Check size={12} className="text-white" strokeWidth={3} />}
                  <input
                    type="checkbox"
                    checked={form.consent}
                    onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                    className="sr-only"
                    data-testid="sp-consent-checkbox"
                  />
                </span>
                <span className="text-xs text-zinc-400 leading-relaxed">
                  Ich stimme der Verarbeitung meiner Daten zur Bearbeitung dieser Session-Anfrage zu (
                  <Link to="/datenschutz" target="_blank" className="text-white underline decoration-[#ff0033]/60 hover:decoration-[#ff0033] underline-offset-2">
                    Datenschutz
                  </Link>
                  ). *
                </span>
              </label>

              {/* Voraussetzungen vor Ort — wichtig hervorgehoben */}
              <div
                data-testid="studio-requirements"
                className="relative mt-7 border border-[#ff0033]/40 bg-[#ff0033]/[0.06] p-5 md:p-6"
              >
                <div className="absolute top-0 left-0 right-0 h-px hairline-red opacity-80" />
                <div className="flex items-center gap-3 mb-3">
                  <span className="red-led" />
                  <span className="text-[10px] tracking-[0.4em] uppercase text-[#ff0033] font-bold">
                    Wichtig · Voraussetzungen vor Ort
                  </span>
                </div>
                <p className="text-sm text-zinc-200 leading-relaxed">
                  Bitte nur anfragen, wenn bei dir vor Ort ein <strong className="text-white">geschlossener Raum mit Steckdose, Strom und Licht</strong> vorhanden ist und die Akustik halbwegs in Ordnung ist (kein Park, kein offener Außenbereich, kein extremer Halleffekt). Wir bringen Mikrofon, Interface und Laptop mit — den Raum musst du stellen.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-7">
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-600 leading-relaxed">
                  Session kostenpflichtig · Antwort innerhalb 48h
                </div>
                <button
                  type="submit"
                  data-testid="sp-submit"
                  disabled={submitting}
                  className="group relative inline-flex items-center gap-3 bg-white text-black px-7 py-4 text-[11px] font-bold tracking-[0.3em] uppercase overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-[0_0_30px_rgba(255,0,51,0.4)] transition-all"
                >
                  <span className="relative z-10 transition-colors group-hover:text-white">
                    {submitting ? "Sende…" : "Session anfragen"}
                  </span>
                  <ArrowUpRight size={14} className="relative z-10 group-hover:text-white group-hover:rotate-45 transition-all duration-500" />
                  <span className="absolute inset-0 bg-[#ff0033] translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
                </button>
              </div>
            </form>
          )}

          <div className="mt-10 text-center text-[10px] tracking-[0.4em] uppercase text-zinc-600">
            · Allgemeine Anfragen bitte über das <Link to="/#kontakt" className="text-white hover:text-[#ff0033] underline decoration-[#ff0033]/40 underline-offset-2">Kontaktformular</Link> ·
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        .booking-input {
          width: 100%;
          background: transparent;
          border: 0;
          border-bottom: 1px solid rgba(255,255,255,0.15);
          padding: 12px 0;
          color: #fff;
          font-size: 15px;
          font-family: 'Manrope', sans-serif;
          font-weight: 500;
          outline: none;
          transition: border-color .3s, box-shadow .3s;
        }
        .booking-input::placeholder { color: #52525b; }
        .booking-input:focus { border-color: rgba(255,0,51,0.6); box-shadow: 0 10px 20px -10px rgba(255,0,51,0.25); }
        .booking-input[type="date"] { color: #fff; }
        .booking-input[type="date"]::-webkit-calendar-picker-indicator { filter: invert(0.5) sepia(1) saturate(8) hue-rotate(330deg); cursor: pointer; }
      `}</style>
    </div>
  );
}

const BookingField = ({ label, full, icon: Icon, children }) => (
  <label className={`block ${full ? "md:col-span-2" : ""}`}>
    <span className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-3">
      {Icon && <Icon size={12} className="text-[#ff0033] shrink-0" />}
      {label}
    </span>
    {children}
  </label>
);
